import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { scoreService, BulletinRequest, BulletinResponse } from '../../services/scoreService';
import { studentService, Student } from '../../services/studentService';
import { classeService, Classe } from '../../services/classeService';
import { useRights } from '../../hooks/useRights';
import './Bulletin.css';

const Bulletin: React.FC = () => {
  const { t } = useTranslation();
  const { classId } = useParams<{ classId: string }>();
  const { hasRight } = useRights();
  const [classe, setClasse] = useState<Classe | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Bulletin data
  const [bulletin, setBulletin] = useState<BulletinResponse | null>(null);

  useEffect(() => {
    if (classId) {
      loadClass();
      loadStudents();
    }
  }, [classId]);

  const loadClass = async () => {
    if (!classId) return;
    try {
      const data = await classeService.getClasseById(classId);
      setClasse(data);
    } catch (e: any) {
      console.error('Failed to load class:', e);
    }
  };

  const loadStudents = async () => {
    if (!classId) return;
    try {
      const response = await studentService.searchStudents({
        page: 0,
        size: 1000,
        filters: { classId },
      });
      setStudents(response.content);
    } catch (e: any) {
      console.error('Failed to load students:', e);
    }
  };

  const handleGenerate = async () => {
    // Validation
    if (!selectedStudentId) {
      setError(t('bulletin.selectStudent'));
      return;
    }
    if (!startDate) {
      setError(t('bulletin.selectStartDate'));
      return;
    }
    if (!endDate) {
      setError(t('bulletin.selectEndDate'));
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError(t('bulletin.invalidDateRange'));
      return;
    }

    const request: BulletinRequest = {
      studentId: selectedStudentId,
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
    };

    try {
      setLoading(true);
      setError('');
      const data = await scoreService.generateBulletin(request);
      setBulletin(data);
    } catch (e: any) {
      setError(e.response?.data?.message || t('bulletin.failedToGenerate'));
      console.error('Generate bulletin error:', e);
      setBulletin(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString();
  };

  return (
    <div className="bulletin-container">
      <div className="bulletin-header">
        <h2>{t('bulletin.title')} - {classe?.name || ''}</h2>
      </div>

      <div className="bulletin-form-card">
        <h3>{t('bulletin.generateBulletin')}</h3>

        {error && <div className="error-message">{error}</div>}

        <div className="form-row">
          <div className="form-field">
            <label>{t('bulletin.student')} *</label>
            <select
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
              disabled={loading}
            >
              <option value="">{t('bulletin.selectStudent')}</option>
              {students.map((student) => (
                <option key={student.id} value={student.id}>
                  {student.firstName} {student.lastName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>{t('bulletin.startDate')} *</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="form-field">
            <label>{t('bulletin.endDate')} *</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-actions">
          {hasRight('bulletins:c') && (
            <button
              onClick={handleGenerate}
              className="btn-generate"
              disabled={loading}
            >
              {loading ? t('common.loading') : t('bulletin.generate')}
            </button>
          )}
        </div>
      </div>

      {bulletin && (
        <div className="bulletin-result-card">
          <div className="bulletin-header-section">
            <h3>{t('bulletin.reportCard')}</h3>
            <div className="student-info">
              <p><strong>{t('bulletin.studentName')}:</strong> {bulletin.studentFirstName} {bulletin.studentLastName}</p>
              <p><strong>{t('bulletin.period')}:</strong> {formatDate(bulletin.startDate)} - {formatDate(bulletin.endDate)}</p>
            </div>
          </div>

          <div className="bulletin-courses-table">
            <table>
              <thead>
                <tr>
                  <th>{t('bulletin.course')}</th>
                  <th>{t('bulletin.factor')}</th>
                  <th>{t('bulletin.examScores')}</th>
                  <th>{t('bulletin.average')}</th>
                </tr>
              </thead>
              <tbody>
                {bulletin.courses.map((course) => (
                  <tr key={course.courseId}>
                    <td>{course.courseName}</td>
                    <td>{course.factor.toFixed(1)}</td>
                    <td>
                      <div className="score-pills">
                        {course.scores.map((score, index) => (
                          <span key={index} className="score-pill">
                            {score.toFixed(2)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="average-cell">
                      <strong>{course.average.toFixed(2)}/20</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bulletin-summary">
            <div className="weighted-average">
              <h4>{t('bulletin.finalWeightedAverage')}</h4>
              <div className="average-value">{bulletin.weightedAverage.toFixed(2)}/20</div>
            </div>
          </div>

          <div className="bulletin-actions">
            <button onClick={() => window.print()} className="btn-print">
              {t('bulletin.print')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bulletin;

